import { createServerFn } from '@tanstack/react-start';
import { createClient } from '@supabase/supabase-js';
import type { Database } from '@/integrations/supabase/types';
import { multiSearchDeals, tsEsc } from '@/lib/typesense.server';

export type NavCategory = { id: string; slug: string; name: string; parent_id: string | null; count: number };
export type NavDeal = { slug: string; title: string; image_url: string; price: number; original_price: number; discount_percent: number; store: string };

function publicDb() {
  const key = process.env['SUPABASE_PUBLISHABLE_KEY'];
  const url = process.env['SUPABASE_URL'];
  if (!key || !url) return null;
  return createClient<Database>(url, key, {
    auth: { persistSession: false },
    global: { fetch: (input, init) => {
      const headers = new Headers(init?.headers);
      if (key.startsWith('sb_') && headers.get('Authorization') === `Bearer ${key}`) headers.delete('Authorization');
      headers.set('apikey', key);
      return fetch(input, { ...init, headers });
    } },
  });
}

let navCache: { at: number; rows: NavCategory[] } | null = null;
export const getNavCategories = createServerFn({ method: 'POST' }).handler(async (): Promise<NavCategory[]> => {
  if (navCache && Date.now() - navCache.at < 30 * 60_000) return navCache.rows;
  const rows = await loadNavCategories().catch(() => [] as NavCategory[]);
  if (rows.length) navCache = { at: Date.now(), rows };
  return rows.length ? rows : navCache?.rows ?? [];
});

async function loadNavCategories(): Promise<NavCategory[]> {
  const db = publicDb();
  if (!db) return [];
  const [{ data: cats }, { data: facets }] = await Promise.all([
    db.from('product_categories').select('id,slug,name,parent_id,sort_order').order('sort_order'),
    db.rpc('deal_facets', {}),
  ]);
  if (!cats || !facets) return [];
  const counts = new Map<string, number>();
  for (const row of facets) {
    if (row.category_id) counts.set(row.category_id, (counts.get(row.category_id) ?? 0) + Number(row.n));
  }
  const roots = cats.filter(c => !c.parent_id);
  const live = new Set(roots.filter(root => (counts.get(root.id) ?? 0) + cats.filter(c => c.parent_id === root.id).reduce((sum, c) => sum + (counts.get(c.id) ?? 0), 0) > 0).map(c => c.id));
  return cats.filter(c => c.parent_id ? live.has(c.parent_id) && (counts.get(c.id) ?? 0) > 0 : live.has(c.id))
    .map(c => ({ id: c.id, slug: c.slug, name: c.name, parent_id: c.parent_id, count: counts.get(c.id) ?? 0 }));
}

const clean = (s: unknown) => String(s ?? '').toLowerCase().replace(/[^a-z0-9-]/g, '').slice(0, 80);

// Um item por loja primeiro; repete loja só para completar.
function diversify<T extends { store: string }>(rows: T[], n = 4): T[] {
  const seen = new Set<string>(); const first: T[] = []; const rest: T[] = [];
  for (const r of rows) { if (seen.has(r.store)) rest.push(r); else { seen.add(r.store); first.push(r); } }
  return [...first, ...rest].slice(0, n);
}

export type NavDealsResult = { mode: 'deals' | 'products'; items: NavDeal[] };

export const getNavDeals = createServerFn({ method: 'POST' })
  .inputValidator((input: { category: string; sub?: string } | string) => typeof input === 'string' ? { category: clean(input), sub: '' } : { category: clean(input?.category), sub: clean(input?.sub) })
  .handler(async ({ data }): Promise<NavDealsResult> => {
    const key = `${data.category}|${data.sub}`;
    const hit = dealsCache.get(key);
    if (hit && Date.now() - hit.at < 5 * 60_000) return hit.res;
    const res = await loadNavDeals(data).catch(() => ({ mode: 'deals', items: [] } as NavDealsResult));
    if (res.items.length) { dealsCache.set(key, { at: Date.now(), res }); if (dealsCache.size > 500) dealsCache.delete(dealsCache.keys().next().value!); return res; }
    return hit?.res ?? res;
  });

const dealsCache = new Map<string, { at: number; res: NavDealsResult }>();
async function loadNavDeals(data: { category: string; sub: string }): Promise<NavDealsResult> {
  {
    const empty: NavDealsResult = { mode: 'deals', items: [] };
    const db = publicDb();
    if (!db || !data.category) return empty;
    const { data: root } = await db.from('product_categories').select('id').eq('slug', data.category).is('parent_id', null).maybeSingle();
    if (!root) return empty;
    let ids: string[];
    if (data.sub) {
      const { data: sub } = await db.from('product_categories').select('id').eq('slug', data.sub).eq('parent_id', root.id).maybeSingle();
      if (!sub) return empty;
      ids = [sub.id];
    } else {
      const { data: children } = await db.from('product_categories').select('id').eq('parent_id', root.id);
      ids = [root.id, ...(children ?? []).map(c => c.id)];
    }
    const freshSince = new Date(Date.now() - 10 * 86400_000).toISOString();
    // Typesense primeiro: o índice já tem categoria, desconto, imagem e loja (10–25ms).
    const freshTs = Math.floor((Date.now() - 10 * 86400_000) / 1000);
    const catFilter = `category_id:[${ids.map(tsEsc).join(',')}] && available:true && image_missing:false && checked_at:>=${freshTs}`;
    const tsMap = (hits: any[]): NavDeal[] => hits.map(h => h.document).filter(d => d?.slug && d?.image_url && Number(d?.price) > 0)
      .map(d => ({ slug: String(d.slug), title: String(d.title ?? ''), image_url: String(d.image_url), price: Number(d.price), original_price: Number(d.original_price ?? 0), discount_percent: Number(d.discount_percent ?? 0), store: String(d.brand_name ?? '') }));
    const tsResults = await multiSearchDeals([
      { q: '*', filter_by: `${catFilter} && discount_percent:>=5 && discount_percent:<=90`, sort_by: 'discount_percent:desc', per_page: 60 },
      { q: '*', filter_by: catFilter, sort_by: 'checked_at:desc', per_page: 60 },
    ]);
    if (tsResults) {
      const promo = tsMap(tsResults[0]?.hits ?? []).filter(d => d.original_price > d.price);
      if (promo.length) return { mode: 'deals', items: diversify(promo) };
      return { mode: 'products', items: diversify(tsMap(tsResults[1]?.hits ?? [])) };
    }
    // Fallback: banco (inalterado).
    const base = () => db.from('deal_products')
      .select('slug,title,image_url,price,original_price,discount_percent,brand:brands!inner(name,is_active,network_paused,deeplink_inactive,merged_into_id)')
      .in('category_id', ids).eq('available', true).is('parent_deal_id', null).gte('checked_at', freshSince)
      .not('image_url', 'is', null)
      .eq('brand.is_active', true).eq('brand.network_paused', false).eq('brand.deeplink_inactive', false)
      .is('brand.merged_into_id', null);
    type Row = { slug: string | null; title: string; image_url: string; price: number; original_price: number | null; discount_percent: number | null; brand: { name: string } };
    const map = (rows: Row[]) => rows.filter(d => d.slug && d.image_url && Number(d.price) > 0)
      .map(d => ({ slug: d.slug ?? '', title: d.title, image_url: d.image_url, price: Number(d.price), original_price: Number(d.original_price ?? 0), discount_percent: Number(d.discount_percent ?? 0), store: d.brand.name }));
    const { data: deals } = await base().gte('discount_percent', 5).lte('discount_percent', 90)
      .order('discount_percent', { ascending: false }).limit(60);
    const promo = map((deals ?? []) as unknown as Row[]).filter(d => d.original_price > d.price);
    if (promo.length) return { mode: 'deals', items: diversify(promo) };
    const { data: prods } = await base().order('checked_at', { ascending: false }).limit(60);
    return { mode: 'products', items: diversify(map((prods ?? []) as unknown as Row[])) };
  }
}
